"use client"

import { useEffect, useState } from "react"
import { observer } from "mobx-react-lite"
import { useAppContext } from "../../../shared/functions/Context"
import type { IUser } from "../../../shared/models/User"
import { Building2, TrendingUp, TrendingDown, Activity, Award } from "lucide-react"

interface RaterData {
  ratings: {
    [key: string]: { [key: string]: number | number[] }
  }
  stopDoing: string
  startDoing: string
  continueDoing: string
}

interface GroupedRating {
  rateeId: string
  raterId: string
  raterData: RaterData
  date: string
}

interface GroupedRatings {
  [rateeId: string]: GroupedRating[]
}

const Department = observer(() => {
  const { api, store } = useAppContext()
  const [loading, setLoading] = useState(true)
  const [highestRatedDepartments, setHighestRatedDepartments] = useState<string[]>([])
  const [lowestRatedDepartments, setLowestRatedDepartments] = useState<string[]>([])
  const [departmentAverages, setDepartmentAverages] = useState<{
    [departmentName: string]: number
  }>({})

  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([api.user.getAll(), api.department.getAll(), api.ratingAssignments.getAll()])
        const users = store.user.all.map((user) => user.asJson)
        const description = store.ratingAssignments.all.find((rate) => rate.asJson.status === "In Progress")?.asJson
          .description

        if (description && users.length) {
          const uids = store.user.all.map((user) => user.asJson.uid)
          await api.valueRating.getAllNew(uids, description)
        }
      } catch (error) {
        console.error("Error fetching data:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [api, store])

  useEffect(() => {
    if (!loading) {
      const calculateDepartmentAverages = () => {
        const valueRatings = store.valueRating.all.map((rating) => rating.asJson)
        const groupedRatings: GroupedRatings = valueRatings.reduce<GroupedRatings>((acc, rating) => {
          const rateeId = rating.rateeId
          const raterEntries = Object.entries(rating.values)
          raterEntries.forEach(([raterId, raterData]) => {
            acc[rateeId] = acc[rateeId] || []
            acc[rateeId].push({
              rateeId,
              raterId,
              raterData: raterData as RaterData,
              date: rating.date.toString(),
            })
          })
          return acc
        }, {})

        const departmentAverages: { [departmentName: string]: number[] } = {}

        Object.entries(groupedRatings).forEach(([rateeId, ratings]) => {
          const userDetails = getUserDetails(rateeId)
          const departmentName = userDetails ? getDepartmentName(userDetails.department) : undefined

          if (departmentName) {
            ratings.forEach((rating) => {
              let totalRatings = 0
              let ratingCounter = 0
              Object.entries(rating.raterData.ratings).forEach(([valueName, statements]) => {
                let totalValueRating = 0
                let valueRatingCount = 0
                Object.values(statements).forEach((ratingDetail: any) => {
                  if (Array.isArray(ratingDetail)) {
                    totalValueRating += ratingDetail.reduce((sum, current) => sum + current, 0)
                    valueRatingCount += ratingDetail.length
                  } else {
                    totalValueRating += ratingDetail
                    valueRatingCount += 1
                  }
                })
                const averageRating = valueRatingCount > 0 ? totalValueRating / valueRatingCount : 0
                totalRatings += averageRating
                ratingCounter += 1
              })
              const overallAverageRating = ratingCounter > 0 ? totalRatings / ratingCounter : 0
              if (departmentAverages[departmentName]) {
                departmentAverages[departmentName].push(overallAverageRating)
              } else {
                departmentAverages[departmentName] = [overallAverageRating]
              }
            })
          }
        })

        const averagedDepartments: { [departmentName: string]: number } = {}
        Object.entries(departmentAverages).forEach(([departmentName, ratings]) => {
          const averageRating = ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length
          averagedDepartments[departmentName] = averageRating
        })

        setDepartmentAverages(averagedDepartments)

        const departmentNames = Object.keys(averagedDepartments)
        const highestRated = departmentNames.reduce((highest, current) => {
          return averagedDepartments[current] > averagedDepartments[highest] ? current : highest
        }, departmentNames[0])

        const lowestRated = departmentNames.reduce((lowest, current) => {
          return averagedDepartments[current] < averagedDepartments[lowest] ? current : lowest
        }, departmentNames[0])

        setHighestRatedDepartments([highestRated])
        setLowestRatedDepartments([lowestRated])
      }

      calculateDepartmentAverages()
    }
  }, [loading, store.valueRating.all])

  const getUserDetails = (userId: string): IUser | null => {
    const user = store.user.all.find((user) => user.asJson.uid === userId)?.asJson
    return user || null
  }

  const getDepartmentName = (departmentId: string): string => {
    const department = store.department.all.find((department) => department.asJson.id === departmentId)?.asJson
    return department ? (department.name ?? "") : ""
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 w-full">
        <div className="relative">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200"></div>
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent absolute top-0 left-0"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <Activity className="h-5 w-5 text-blue-600 animate-pulse" />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-blue-100 rounded-lg">
          <Building2 className="h-5 w-5 text-blue-600" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Department Performance</h2>
          <p className="text-slate-600">Performance analysis by department</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Highest Rated */}
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-200/50">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-green-100 rounded-lg">
              <TrendingUp className="h-5 w-5 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-green-800">Highest Rated Department</h3>
          </div>
          {highestRatedDepartments.map((departmentName) => (
            <div key={departmentName} className="space-y-2">
              <p className="text-xl font-bold text-green-900">{departmentName}</p>
              <p className="text-green-700">
                Average Rating:{" "}
                <span className="font-semibold">{departmentAverages[departmentName]?.toFixed(2) ?? "N/A"}</span>
              </p>
            </div>
          ))}
        </div>

        {/* Lowest Rated */}
        <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-6 border border-red-200/50">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-red-100 rounded-lg">
              <TrendingDown className="h-5 w-5 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-red-800">Lowest Rated Department</h3>
          </div>
          {lowestRatedDepartments.map((departmentName) => (
            <div key={departmentName} className="space-y-2">
              <p className="text-xl font-bold text-red-900">{departmentName}</p>
              <p className="text-red-700">
                Average Rating:{" "}
                <span className="font-semibold">{departmentAverages[departmentName]?.toFixed(2) ?? "N/A"}</span>
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* All Departments Table */}
      <div className="bg-white rounded-2xl shadow-lg border border-slate-200/50 overflow-hidden">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-slate-100 rounded-lg">
              <Award className="h-5 w-5 text-slate-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-800">All Departments</h3>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left py-4 px-6 font-semibold text-slate-700">Department Name</th>
                <th className="text-left py-4 px-6 font-semibold text-slate-700">Average Rating</th>
                <th className="text-left py-4 px-6 font-semibold text-slate-700">Performance</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(departmentAverages)
                .sort(([, a], [, b]) => b - a)
                .map(([departmentName, averageRating], index) => (
                  <tr
                    key={departmentName}
                    className="border-b border-slate-100 hover:bg-slate-50 transition-colors duration-200"
                  >
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Building2 className="h-4 w-4 text-blue-600" />
                        </div>
                        <span className="font-medium text-slate-800">{departmentName}</span>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span className="text-lg font-bold text-slate-800">
                        {typeof averageRating === "number" && !isNaN(averageRating) ? averageRating.toFixed(2) : "N/A"}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-2">
                        <div className="w-24 bg-slate-200 rounded-full h-2">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${
                              averageRating >= 4 ? "bg-green-500" : averageRating >= 3 ? "bg-yellow-500" : "bg-red-500"
                            }`}
                            style={{ width: `${Math.min((averageRating / 5) * 100, 100)}%` }}
                          />
                        </div>
                        <span
                          className={`text-xs font-medium ${
                            averageRating >= 4
                              ? "text-green-600"
                              : averageRating >= 3
                                ? "text-yellow-600"
                                : "text-red-600"
                          }`}
                        >
                          {averageRating >= 4 ? "Excellent" : averageRating >= 3 ? "Good" : "Needs Improvement"}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
})

export default Department
